import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import DataTable from './DataTable';
import VendorSelect from './VendorSelect';
import SearchableSelect from './SearchableSelect';
import Loader from './Loader';
import QRCodeGenerator from './QRCodeGenerator';
import AssetBulkOperations from './AssetBulkOperations';
import FileUploadComponent from './FileUploadComponent';
import { FileUploadService } from '../services/FileUploadService';
import Alert from './Alert';
import { useAlert } from '../hooks/useAlert';
import './InventoryDisplay.css';
import './Timeline.css';

const API_URL = '/api';

function Assets() {
  const { hasPermission } = useAuth();
  const [assets, setAssets] = useState([]);
  const [assetTypes, setAssetTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    assetTypeId: '',
    vendorId: '',
    purchaseDate: '',
    purchasePrice: '',
    pricePerItem: '',
    quantity: '1',
    description: '',
    attachmentFileName: ''
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showDeadModal, setShowDeadModal] = useState(false);
  const [selectedAssetForDead, setSelectedAssetForDead] = useState(null);
  const [deadItems, setDeadItems] = useState([{ saralAssetId: '', serialNumber: '', notes: '' }]);
  const [showDeadListModal, setShowDeadListModal] = useState(false);
  const [selectedAssetDeadItems, setSelectedAssetDeadItems] = useState([]);
  const [showAssignmentsModal, setShowAssignmentsModal] = useState(false);
  const [selectedAssetAssignments, setSelectedAssetAssignments] = useState([]);
  const [showRepairsModal, setShowRepairsModal] = useState(false);
  const [selectedAssetRepairs, setSelectedAssetRepairs] = useState([]);
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [assetTimeline, setAssetTimeline] = useState([]);
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedAssetForQR, setSelectedAssetForQR] = useState(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [filters, setFilters] = useState({
    assetType: '',
    status: '',
    priceRange: { min: '', max: '' },
    dateRange: { start: '', end: '' }
  });
  const [showFilters, setShowFilters] = useState(false);
  const { alert, showAlert, hideAlert } = useAlert();

  useEffect(() => {
    fetchAssets();
    fetchAssetTypes();
    document.title = 'Assets - Saral Asset Manager';
  }, []);

  const fetchAssets = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/assets`);
      const assets = response.data || [];
      setAssets(assets);
      return assets;
    } catch (error) {
      console.error('Error fetching assets:', error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const fetchAssetTypes = async () => {
    try {
      const response = await axios.get(`${API_URL}/assettypes`);
      setAssetTypes(response.data);
    } catch (error) {
      console.error('Error fetching asset types:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let fileNames = '';
      
      // Upload files if any
      if (selectedFiles.length > 0) {
        const folderName = `asset_${editingId || Date.now()}`;
        try {
          fileNames = await FileUploadService.uploadFiles(selectedFiles, folderName);
        } catch (uploadError) {
          console.error('File upload failed:', uploadError);
          alert('File upload failed. Continuing without attachments.');
        }
      }

      const assetData = {
        ...formData,
        assetTypeId: parseInt(formData.assetTypeId),
        vendorId: formData.vendorId ? parseInt(formData.vendorId) : null,
        purchasePrice: parseFloat(formData.purchasePrice),
        quantity: parseInt(formData.quantity),
        attachmentFileName: fileNames
      };

      if (editingId) {
        await axios.put(`${API_URL}/assets/${editingId}`, {
          ...assetData,
          id: editingId
        });
        handleCloseModal();
        showAlert('success', 'Asset updated successfully!');
      } else {
        await axios.post(`${API_URL}/assets`, assetData);
        handleCloseModal();
        showAlert('success', 'Asset added successfully!');
      }
      fetchAssets();
    } catch (error) {
      console.error('Error saving asset:', error);
      const errorMsg = error.response?.data || error.message || 'Failed to save asset';
      showAlert('danger', `Error: ${errorMsg}`);
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = () => {
    setFormData({
      name: '',
      assetTypeId: '',
      vendorId: '',
      purchaseDate: '',
      purchasePrice: '',
      pricePerItem: '',
      quantity: '1',
      description: '',
      attachmentFileName: ''
    });
    setEditingId(null);
    setSelectedFiles([]);
    setShowModal(true);
  };

  const handleEdit = (asset) => {
    const pricePerItem = asset.quantity > 0 ? (asset.purchasePrice / asset.quantity).toFixed(2) : '';
    setFormData({
      name: asset.name,
      assetTypeId: asset.assetTypeId,
      vendorId: asset.vendorId || '',
      purchaseDate: asset.purchaseDate.split('T')[0],
      purchasePrice: asset.purchasePrice,
      pricePerItem: pricePerItem,
      quantity: asset.quantity || '1',
      description: asset.description || '',
      attachmentFileName: asset.attachmentFileName || ''
    });
    setEditingId(asset.id);
    setSelectedFiles([]);
    setShowModal(true);
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API_URL}/assets/${deleteId}`);
      setShowDeleteModal(false);
      setDeleteId(null);
      showAlert('success', 'Asset deleted successfully!');
      fetchAssets();
    } catch (error) {
      console.error('Error deleting asset:', error);
      const errorMsg = error.response?.data || error.message || 'Failed to delete asset';
      showAlert('danger', `Error: ${errorMsg}`);
    }
  };

  const handleOpenDeadModal = (asset) => {
    setSelectedAssetForDead(asset);
    setDeadItems([{ saralAssetId: '', serialNumber: '', notes: '' }]);
    setShowDeadModal(true);
  };

  const handleSubmitDeadItems = async (e) => {
    e.preventDefault();

    const items = deadItems
      .map(item => ({
        saralAssetId: item.saralAssetId.trim(),
        serialNumber: item.serialNumber.trim(),
        notes: item.notes.trim()
      }))
      .filter(item => item.serialNumber);

    if (!selectedAssetForDead || items.length === 0) {
      showAlert('danger', 'Add at least one serial number.');
      return;
    }

    try {
      await axios.post(`${API_URL}/assets/${selectedAssetForDead.id}/dead-items`, { items });
      setShowDeadModal(false);
      setSelectedAssetForDead(null);
      setDeadItems([{ saralAssetId: '', serialNumber: '', notes: '' }]);
      showAlert('success', `${items.length} asset unit(s) marked dead successfully!`);
      fetchAssets();
    } catch (error) {
      console.error('Error marking asset units as dead:', error);
      const errorMsg = error.response?.data || error.message || 'Failed to mark asset units as dead';
      showAlert('danger', `Error: ${errorMsg}`);
    }
  };

  const handleViewDeadItems = async (asset) => {
    try {
      const response = await axios.get(`${API_URL}/assets/${asset.id}/dead-items`);
      setSelectedAssetForDead(asset);
      setSelectedAssetDeadItems(response.data || []);
      setShowDeadListModal(true);
    } catch (error) {
      console.error('Error fetching dead asset items:', error);
      const errorMsg = error.response?.data || error.message || 'Failed to load dead asset items';
      showAlert('danger', `Error: ${errorMsg}`);
    }
  };

  const handleRestoreDeadItem = async (deadItem) => {
    if (!selectedAssetForDead) return;
    const confirmRestore = window.confirm(`Restore serial "${deadItem.serialNumber}"?`);
    if (!confirmRestore) return;

    try {
      await axios.delete(`${API_URL}/assets/${selectedAssetForDead.id}/dead-items/${deadItem.id}`);
      showAlert('success', 'Dead asset unit restored successfully!');

      const refreshedAssets = await fetchAssets();
      const updatedAsset = refreshedAssets.find((item) => item.id === selectedAssetForDead.id);
      if (updatedAsset) {
        setSelectedAssetForDead(updatedAsset);
      }
      const response = await axios.get(`${API_URL}/assets/${selectedAssetForDead.id}/dead-items`);
      setSelectedAssetDeadItems(response.data || []);
    } catch (error) {
      console.error('Error restoring dead asset unit:', error);
      const errorMsg = error.response?.data || error.message || 'Failed to restore dead asset unit';
      showAlert('danger', `Error: ${errorMsg}`);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      name: '',
      assetTypeId: '',
      vendorId: '',
      purchaseDate: '',
      purchasePrice: '',
      pricePerItem: '',
      quantity: '1',
      description: '',
      attachmentFileName: ''
    });
    setEditingId(null);
    setSelectedFiles([]);
  };

  const handleViewAssignments = async (assetId) => {
    try {
      const response = await axios.get(`${API_URL}/assetassignments`);
      const assetAssignments = response.data.filter(assignment => 
        assignment.assetId === assetId && assignment.isActive
      );
      setSelectedAssetAssignments(assetAssignments);
      setShowAssignmentsModal(true);
    } catch (error) {
      console.error('Error fetching assignments:', error);
    }
  };

  const handleViewRepairs = async (assetId) => {
    try {
      const response = await axios.get(`${API_URL}/repairs`);
      const assetRepairs = response.data.filter(repair => repair.assetId === assetId);
      setSelectedAssetRepairs(assetRepairs);
      setShowRepairsModal(true);
    } catch (error) {
      console.error('Error fetching repairs:', error);
    }
  };

  const handleViewTimeline = async (assetId) => {
    try {
      const [assignmentsRes, repairsRes] = await Promise.all([
        axios.get(`${API_URL}/assetassignments`),
        axios.get(`${API_URL}/repairs`)
      ]);
      
      const assignments = assignmentsRes.data.filter(a => a.assetId === assetId);
      const repairs = repairsRes.data.filter(r => r.assetId === assetId);
      
      const timeline = [
        ...assignments.map(a => ({
          type: 'assignment',
          date: a.assignedDate,
          title: `Assigned to ${a.employee?.name}`,
          details: `Saral ID: ${a.saralAssetId}, Serial: ${a.serialNumber}`,
          status: a.isActive ? 'Active' : 'Returned',
          returnDate: a.returnDate
        })),
        ...repairs.map(r => ({
          type: 'repair',
          date: r.sentDate,
          title: `Sent for Repair`,
          details: `Issue: ${r.issueDescription}, Vendor: ${r.vendor?.name}`,
          status: r.returnDate ? 'Completed' : 'In Progress',
          returnDate: r.returnDate,
          cost: r.repairCost
        }))
      ].sort((a, b) => new Date(b.date) - new Date(a.date));
      
      setAssetTimeline(timeline);
      setShowTimelineModal(true);
    } catch (error) {
      console.error('Error fetching timeline:', error);
    }
  };

  const filteredAssets = assets.filter(asset => {
    if (filters.assetType && asset.assetTypeId != filters.assetType) return false;
    if (filters.status && asset.status !== filters.status) return false;
    if (filters.priceRange.min && asset.purchasePrice < parseFloat(filters.priceRange.min)) return false;
    if (filters.priceRange.max && asset.purchasePrice > parseFloat(filters.priceRange.max)) return false;
    if (filters.dateRange.start && new Date(asset.purchaseDate) < new Date(filters.dateRange.start)) return false;
    if (filters.dateRange.end && new Date(asset.purchaseDate) > new Date(filters.dateRange.end)) return false;
    return true;
  });

  const columns = [
    { 
      field: 'name', 
      label: 'Name'
    },
    { 
      field: 'assetType', 
      label: 'Type',
      render: (value) => value?.name || 'N/A',
      sortValue: (value) => value?.name || ''
    },
    { 
      field: 'quantity', 
      label: 'Inventory',
      render: (value, item) => {
        const assigned = item.assignedCount || 0;
        const dead = item.deadCount || 0;
        const remaining = value - assigned - dead;
        const inRepair = item.inRepairCount || 0;
        
        return (
          <div className="inventory-display">
            <div className="fw-bold mb-1">{value} Total</div>
            <div className="d-flex flex-wrap gap-1">
              <div className="inventory-item available">
                <i className="fas fa-check-circle"></i> {remaining}
              </div>
              {assigned > 0 && (
                <div 
                  className="inventory-item assigned clickable" 
                  onClick={() => handleViewAssignments(item.id)}
                >
                  <i className="fas fa-user"></i> {assigned}
                </div>
              )}
              {item.repairCount > 0 && (
                <div 
                  className="inventory-item repairs clickable" 
                  onClick={() => handleViewRepairs(item.id)}
                >
                  <i className="fas fa-wrench"></i> {item.repairCount}
                </div>
              )}
              {inRepair > 0 && (
                <div className="inventory-item in-repair">
                  <i className="fas fa-tools"></i> {inRepair}
                </div>
              )}
              {dead > 0 && (
                <div
                  className="inventory-item clickable"
                  style={{ backgroundColor: '#dc3545', color: 'white', cursor: 'pointer' }}
                  onClick={() => handleViewDeadItems(item)}
                >
                  <i className="fas fa-ban"></i> {dead}
                </div>
              )}
            </div>
          </div>
        );
      }
    },
    { 
      field: 'purchaseDate', 
      label: 'Purchase Date',
      render: (value) => new Date(value).toLocaleDateString(),
      sortValue: (value) => new Date(value).getTime()
    },
    ...(hasPermission('canViewPricing') ? [{ 
      field: 'purchasePrice', 
      label: 'Total Price',
      render: (value) => `₹${value}`
    }, {
      field: 'pricePerItem',
      label: 'Per Item',
      render: (value) => `₹${value}`
    }] : []),
    { 
      field: 'status', 
      label: 'Status',
      render: (value) => {
        const badgeClass =
          value === 'Available' ? 'bg-success' :
          value === 'Dead' ? 'bg-danger' :
          value === 'InRepair' ? 'bg-warning text-dark' :
          'bg-warning';

        return (
          <span className={`badge ${badgeClass}`}>
            {value}
          </span>
        );
      }
    },
    {
      field: 'attachmentFileName',
      label: 'Files',
      render: (value, item) => {
        if (!value) return '-';
        
        const fileNames = value.split(',').filter(f => f.trim());
        return (
          <button 
            className="btn btn-sm btn-outline-primary"
            onClick={() => FileUploadService.downloadFiles(`asset_${item.id}`)}
          >
            <i className="fas fa-download me-1"></i>
            {fileNames.length} file{fileNames.length > 1 ? 's' : ''}
          </button>
        );
      }
    }
  ];

  return (
    <div className="container-fluid">
      <Alert show={alert.show} type={alert.type} message={alert.message} onClose={hideAlert} />
      
      <div className="page-header">
        <h1 className="page-title mb-0">Assets</h1>
        <div className="page-header-actions">
          <button className="btn btn-sm btn-outline-info" onClick={() => setShowBulkModal(true)}>
            <i className="fas fa-upload me-1"></i><span className="d-none d-sm-inline">Bulk Operations</span><span className="d-sm-none">Bulk</span>
          </button>
          <button className="btn btn-sm btn-primary" onClick={handleAdd}>
            <i className="fas fa-plus me-1"></i><span className="d-none d-sm-inline">Add Asset</span><span className="d-sm-none">Add</span>
          </button>
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-3">
        <div className="card-header bg-light d-flex justify-content-between align-items-center">
          <h6 className="mb-0">Filters</h6>
          <button 
            className="btn btn-sm btn-outline-primary"
            onClick={() => setShowFilters(!showFilters)}
          >
            <i className={`fas fa-${showFilters ? 'minus' : 'plus'}`}></i>
          </button>
        </div>
        {showFilters && (
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-3">
                <label className="form-label">Asset Type</label>
                <SearchableSelect
                  options={[
                    { value: '', label: 'All Types' },
                    ...assetTypes.map(type => ({ value: type.id, label: type.name }))
                  ]}
                  value={filters.assetType}
                  onChange={(e) => setFilters({...filters, assetType: e.target.value})}
                  placeholder="Select Asset Type"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Status</label>
                <SearchableSelect
                  options={[
                    { value: '', label: 'All Status' },
                    { value: 'Available', label: 'Available' },
                    { value: 'Assigned', label: 'Assigned' },
                    { value: 'InRepair', label: 'In Repair' },
                    { value: 'Dead', label: 'Dead' }
                  ]}
                  value={filters.status}
                  onChange={(e) => setFilters({...filters, status: e.target.value})}
                  placeholder="Select Status"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Price Range</label>
                <div className="d-flex gap-2">
                  <input 
                    type="number" 
                    className="form-control" 
                    placeholder="Min"
                    value={filters.priceRange.min}
                    onChange={(e) => setFilters({...filters, priceRange: {...filters.priceRange, min: e.target.value}})}
                  />
                  <input 
                    type="number" 
                    className="form-control" 
                    placeholder="Max"
                    value={filters.priceRange.max}
                    onChange={(e) => setFilters({...filters, priceRange: {...filters.priceRange, max: e.target.value}})}
                  />
                </div>
              </div>
              <div className="col-md-3">
                <label className="form-label">Purchase Date</label>
                <div className="d-flex gap-2">
                  <input 
                    type="date" 
                    className="form-control"
                    value={filters.dateRange.start}
                    onChange={(e) => setFilters({...filters, dateRange: {...filters.dateRange, start: e.target.value}})}
                  />
                  <input 
                    type="date" 
                    className="form-control"
                    value={filters.dateRange.end}
                    onChange={(e) => setFilters({...filters, dateRange: {...filters.dateRange, end: e.target.value}})}
                  />
                </div>
              </div>
            </div>
            <div className="mt-3">
              <button 
                className="btn btn-secondary btn-sm"
                onClick={() => setFilters({assetType: '', status: '', priceRange: {min: '', max: ''}, dateRange: {start: '', end: ''}})}
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-body">
          {loading ? (
            <div className="table-loader">
              <Loader text="Loading assets..." size="medium" />
            </div>
          ) : (
            <DataTable
              data={filteredAssets}
              columns={columns}
              onEdit={handleEdit}
              onDelete={handleDelete}
              customActions={(item) => (
                (item.quantity - (item.assignedCount || 0) - (item.deadCount || 0)) > 0 && (
                <li>
                  <button className="dropdown-item text-danger" onClick={() => handleOpenDeadModal(item)}>
                    <i className="fas fa-ban me-2"></i>Mark Dead Units
                  </button>
                </li>
                )
              )}
            />
          )}
        </div>
      </div>

      {/* Modal */}
      <div className={`modal fade ${showModal ? 'show d-block' : ''}`} style={{backgroundColor: showModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog modal-xl" style={{ maxWidth: '1400px' }}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-laptop me-2"></i>
                {editingId ? 'Edit Asset' : 'Add New Asset'}
              </h5>
              <button type="button" className="btn-close" onClick={handleCloseModal}></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Asset Name</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter asset name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Asset Type</label>
                    <SearchableSelect
                      options={assetTypes.map(type => ({ value: type.id, label: type.name }))}
                      value={formData.assetTypeId}
                      onChange={(e) => setFormData({ ...formData, assetTypeId: e.target.value })}
                      placeholder="Select Asset Type"
                      required
                      onQuickAdd={async (name) => {
                        try {
                          const response = await axios.post('/api/assettypes', {
                            name: name,
                            description: `${name} for office use`
                          });
                          await fetchAssetTypes();
                          return { value: response.data.id, label: response.data.name };
                        } catch (error) {
                          console.error('Error adding asset type:', error);
                          throw error;
                        }
                      }}
                      addButtonText="Add Asset Type"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Vendor</label>
                    <VendorSelect
                      value={formData.vendorId}
                      onChange={(e) => setFormData({ ...formData, vendorId: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Purchase Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={formData.purchaseDate}
                      onChange={(e) => setFormData({ ...formData, purchaseDate: e.target.value })}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Quantity</label>
                    <input
                      type="number"
                      min="1"
                      className="form-control"
                      placeholder="Enter quantity"
                      value={formData.quantity}
                      onChange={(e) => {
                        const quantity = e.target.value;
                        const pricePerItem = formData.pricePerItem;
                        const totalPrice = pricePerItem && quantity ? (parseFloat(pricePerItem) * parseInt(quantity)).toFixed(2) : '';
                        setFormData({ 
                          ...formData, 
                          quantity: quantity,
                          purchasePrice: totalPrice
                        });
                      }}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Price Per Item (₹)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      placeholder="Enter price per item"
                      value={formData.pricePerItem}
                      onChange={(e) => {
                        const pricePerItem = e.target.value;
                        const quantity = formData.quantity;
                        const totalPrice = pricePerItem && quantity ? (parseFloat(pricePerItem) * parseInt(quantity)).toFixed(2) : '';
                        setFormData({ 
                          ...formData, 
                          pricePerItem: pricePerItem,
                          purchasePrice: totalPrice
                        });
                      }}
                      disabled={!hasPermission('canViewPricing')}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Total Purchase Price (₹)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      placeholder="Enter total price or auto-calculated"
                      value={formData.purchasePrice}
                      onChange={(e) => setFormData({ ...formData, purchasePrice: e.target.value })}
                      disabled={!hasPermission('canViewPricing')}
                    />
                    <small className="text-muted">Auto-calculated: {formData.quantity || 1} × ₹{formData.pricePerItem || 0} = ₹{((formData.quantity || 1) * (formData.pricePerItem || 0)).toFixed(2)}</small>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      placeholder="Enter description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    ></textarea>
                  </div>
                  <FileUploadComponent
                    selectedFiles={selectedFiles}
                    setSelectedFiles={setSelectedFiles}
                    existingFiles={editingId && formData.attachmentFileName ? formData.attachmentFileName : null}
                    folderName={editingId ? `asset_${editingId}` : null}
                    label="Upload Purchase Receipt/Bill (Optional)"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className={`btn btn-primary ${saving ? 'btn-loading' : ''}`} disabled={saving}>
                  {saving ? (
                    <Loader size="small" />
                  ) : (
                    <>
                      <i className="fas fa-save me-1"></i>
                      {editingId ? 'Update Asset' : 'Add Asset'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <div className={`modal fade ${showDeleteModal ? 'show d-block' : ''}`} style={{backgroundColor: showDeleteModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-exclamation-triangle me-2 text-warning"></i>
                Confirm Delete
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
            </div>
            <div className="modal-body">
              Are you sure you want to delete this asset? This action cannot be undone.
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button type="button" className="btn btn-danger" onClick={confirmDelete}>
                <i className="fas fa-trash me-1"></i>
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className={`modal fade ${showDeadModal ? 'show d-block' : ''}`} style={{backgroundColor: showDeadModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-ban me-2 text-danger"></i>
                Mark Asset Units as Dead
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowDeadModal(false)}></button>
            </div>
            <form onSubmit={handleSubmitDeadItems}>
              <div className="modal-body">
                {selectedAssetForDead && (
                  <div className="alert alert-light border">
                    <strong>Asset:</strong> {selectedAssetForDead.name}
                    <br />
                    <strong>Available to mark dead:</strong> {selectedAssetForDead.quantity - (selectedAssetForDead.assignedCount || 0) - (selectedAssetForDead.deadCount || 0)}
                  </div>
                )}
                {deadItems.map((item, index) => (
                  <div key={index} className="row g-2 mb-3 align-items-end">
                    <div className="col-md-3">
                      <label className="form-label">Saral Asset ID</label>
                      <input
                        type="text"
                        className="form-control"
                        value={item.saralAssetId}
                        onChange={(e) => {
                          const updated = [...deadItems];
                          updated[index].saralAssetId = e.target.value;
                          setDeadItems(updated);
                        }}
                        placeholder="Optional Saral asset ID"
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Serial Number</label>
                      <input
                        type="text"
                        className="form-control"
                        value={item.serialNumber}
                        onChange={(e) => {
                          const updated = [...deadItems];
                          updated[index].serialNumber = e.target.value;
                          setDeadItems(updated);
                        }}
                        required
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Notes</label>
                      <input
                        type="text"
                        className="form-control"
                        value={item.notes}
                        onChange={(e) => {
                          const updated = [...deadItems];
                          updated[index].notes = e.target.value;
                          setDeadItems(updated);
                        }}
                        placeholder="Optional reason/details"
                      />
                    </div>
                    <div className="col-md-2">
                      <button
                        type="button"
                        className="btn btn-outline-danger w-100"
                        onClick={() => setDeadItems(deadItems.filter((_, itemIndex) => itemIndex !== index))}
                        disabled={deadItems.length === 1}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => setDeadItems([...deadItems, { saralAssetId: '', serialNumber: '', notes: '' }])}
                >
                  <i className="fas fa-plus me-1"></i>Add Another Unit
                </button>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeadModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-danger">
                  <i className="fas fa-ban me-1"></i>Mark Dead
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div className={`modal fade ${showDeadListModal ? 'show d-block' : ''}`} style={{backgroundColor: showDeadListModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-list me-2 text-danger"></i>
                Dead Asset Units{selectedAssetForDead ? ` - ${selectedAssetForDead.name}` : ''}
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowDeadListModal(false)}></button>
            </div>
            <div className="modal-body">
              {selectedAssetDeadItems.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Saral Asset ID</th>
                        <th>Serial Number</th>
                        <th>Notes</th>
                        <th>Marked Dead At</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedAssetDeadItems.map((item) => (
                        <tr key={item.id}>
                          <td>{item.saralAssetId || '-'}</td>
                          <td>{item.serialNumber}</td>
                          <td>{item.notes || '-'}</td>
                          <td>{new Date(item.markedDeadAt).toLocaleString()}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-success"
                              onClick={() => handleRestoreDeadItem(item)}
                            >
                              <i className="fas fa-undo me-1"></i>Restore
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted mb-0">No dead asset units found.</p>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowDeadListModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Assignments Modal */}
      <div className={`modal fade ${showAssignmentsModal ? 'show d-block' : ''}`} style={{backgroundColor: showAssignmentsModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-users me-2"></i>
                Assigned Items
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowAssignmentsModal(false)}></button>
            </div>
            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {selectedAssetAssignments.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Saral Asset ID</th>
                        <th>Serial Number</th>
                        <th>Employee</th>
                        <th>Assigned Date</th>
                        <th>Delivery Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedAssetAssignments.map((assignment) => (
                        <tr key={assignment.id}>
                          <td>{assignment.saralAssetId}</td>
                          <td>{assignment.serialNumber}</td>
                          <td>{assignment.employee?.name || 'N/A'}</td>
                          <td>{new Date(assignment.assignedDate).toLocaleDateString()}</td>
                          <td>
                            <span className={`badge ${
                              assignment.deliveryStatus === 0 ? 'bg-warning text-dark' :
                              assignment.deliveryStatus === 1 ? 'bg-info text-white' :
                              'bg-success text-white'
                            }`}>
                              {assignment.deliveryStatus === 0 ? 'Pending' :
                               assignment.deliveryStatus === 1 ? 'In Transit' : 'Delivered'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted">No active assignments found for this asset.</p>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowAssignmentsModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Repairs Modal */}
      <div className={`modal fade ${showRepairsModal ? 'show d-block' : ''}`} style={{backgroundColor: showRepairsModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-wrench me-2"></i>
                Repair Records
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowRepairsModal(false)}></button>
            </div>
            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {selectedAssetRepairs.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Issue</th>
                        <th>Repair Source</th>
                        <th>Vendor</th>
                        {hasPermission('canViewPricing') && <th>Cost</th>}
                        <th>Sent Date</th>
                        <th>Return Date</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedAssetRepairs.map((repair) => (
                        <tr key={repair.id}>
                          <td>{repair.repairReason || repair.issueDescription || '-'}</td>
                          <td>
                            <span className="badge bg-info">
                              {repair.repairSource === 0 ? 'Office' :
                               repair.repairSource === 1 ? 'Employee Reported' : 'Employee Returned'}
                            </span>
                          </td>
                          <td>{repair.repairVendor || repair.vendor?.name || 'N/A'}</td>
                          {hasPermission('canViewPricing') && <td>₹{repair.repairCost || 0}</td>}
                          <td>{repair.repairDate ? new Date(repair.repairDate).toLocaleDateString() : '-'}</td>
                          <td>{repair.completedDate ? new Date(repair.completedDate).toLocaleDateString() : '-'}</td>
                          <td>
                            <span className={`badge ${repair.completedDate ? 'bg-success' : 'bg-warning text-dark'}`}>
                              {repair.completedDate ? 'Completed' : 'In Progress'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted">No repair records found for this asset.</p>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowRepairsModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Timeline Modal */}
      <div className={`modal fade ${showTimelineModal ? 'show d-block' : ''}`} style={{backgroundColor: showTimelineModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog modal-lg timeline-modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-history me-2"></i>
                Asset Timeline
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowTimelineModal(false)}></button>
            </div>
            <div className="modal-body timeline-modal-body">
              {assetTimeline.length > 0 ? (
                <div className="timeline">
                  {assetTimeline.map((event, index) => (
                    <div key={index} className={`timeline-item ${event.type}`}>
                      <div className="timeline-marker">
                        <i className={`fas ${event.type === 'assignment' ? 'fa-user' : 'fa-wrench'}`}></i>
                      </div>
                      <div className="timeline-content">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <h6 className="mb-1">{event.title}</h6>
                            <p className="text-muted small mb-1">{event.details}</p>
                            <small className="text-muted">
                              {new Date(event.date).toLocaleString()}
                              {event.returnDate && (
                                <span> - {new Date(event.returnDate).toLocaleString()}</span>
                              )}
                            </small>
                          </div>
                          <div className="text-end">
                            <span className={`badge ${event.status === 'Active' || event.status === 'In Progress' ? 'bg-warning' : 'bg-success'}`}>
                              {event.status}
                            </span>
                            {event.cost && hasPermission('canViewPricing') && (
                              <div className="text-muted small mt-1">₹{event.cost}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted">No timeline data available for this asset.</p>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowTimelineModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* QR Code Modal */}
      <div className={`modal fade ${showQRModal ? 'show d-block' : ''}`} style={{backgroundColor: showQRModal ? 'rgba(0,0,0,0.5)' : 'transparent'}}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-qrcode me-2"></i>
                Asset QR Code
              </h5>
              <button type="button" className="btn-close" onClick={() => setShowQRModal(false)}></button>
            </div>
            <div className="modal-body">
              {selectedAssetForQR && (
                <QRCodeGenerator asset={selectedAssetForQR} onClose={() => setShowQRModal(false)} />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Operations Modal */}
      <AssetBulkOperations 
        show={showBulkModal} 
        onHide={() => setShowBulkModal(false)}
        onRefresh={fetchAssets}
      />
    </div>
  );
}

export default Assets;
